import { AppBar, Toolbar, Typography, IconButton, Menu, MenuItem, Box, Badge, Divider } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { useNavigate } from 'react-router-dom';
import { usePatientNutritionNotifications } from '../hooks/usePatientNutritionNotifications';

/**
 * Top App Bar
 * Shared across all portal layouts
 * Shows logo, user info, and logout menu
 */
export const TopAppBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const { notifications, notificationCount } = usePatientNutritionNotifications();
  const isPatient = user?.role === 'PATIENT';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationSelect = (href: string) => {
    handleNotificationClose();
    navigate(href);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
    >
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          CGM Nutrition System
        </Typography>
        
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isPatient && (
              <>
                <IconButton
                  size="large"
                  onClick={handleNotificationMenu}
                  color="inherit"
                  aria-label="nutrition team notifications"
                >
                  <Badge badgeContent={notificationCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                <Menu
                  anchorEl={notificationAnchorEl}
                  open={Boolean(notificationAnchorEl)}
                  onClose={handleNotificationClose}
                  PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 24px)' } }}
                >
                  <MenuItem disabled>
                    <Typography variant="subtitle2">Nutrition Team Updates</Typography>
                  </MenuItem>
                  <Divider />
                  {notifications.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No new nutrition team notifications.
                      </Typography>
                    </MenuItem>
                  ) : (
                    notifications.slice(0, 5).map((notification) => (
                      <MenuItem
                        key={notification.id}
                        onClick={() => handleNotificationSelect(notification.href)}
                        sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {notification.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1 }}>
                            {notification.actionLabel}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Menu>
              </>
            )}
            <Typography variant="body2">
              {user.displayName || user.email}
            </Typography>
            <IconButton
              size="large"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  Role: {user.role}
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};
